import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router, Routes } from '@angular/router';
import { injectShowTextTuiAlert } from '@konnektu/helpers';
import { equal, from, MetastoreService } from '@konnektu/metastore';
import { featureCanMatchGuard } from '@konnektu/multitenant';
import { INSTANCE_CODE } from '@konnektu/tokens';
import { concatMap, of } from 'rxjs';

interface ResolvedDynamicView {
  Id: string;
  Path: string;
  Payload: string;
  Metadata: string;
  ModifiedOn: string | null;
}

const dynamicViewResolver: ResolveFn<ResolvedDynamicView | RedirectCommand> = (
  route
) => {
  const metastore = inject(MetastoreService);
  const tenantCode = inject(INSTANCE_CODE);
  const router = inject(Router);
  const cardId = route.params['id'];
  const showTextTuiAlert = injectShowTextTuiAlert();
  return metastore
    .find<ResolvedDynamicView>(
      from('UIDynamicView')
        .select(['Id', 'Path', 'Payload', 'Metadata', 'ModifiedOn'])
        .where(equal('Id', cardId))
    )
    .pipe(
      concatMap((card) => {
        if (!card) {
          showTextTuiAlert('dynamicEditor.viewNotFound', 'warning');
          return of(
            new RedirectCommand(router.parseUrl(`${tenantCode}/dynamic-views`))
          );
        }
        return of(card);
      })
    );
};

export const DYNAMIC_EDITOR_ROUTES: Routes = [
  {
    path: 'dynamic-views',
    canMatch: [featureCanMatchGuard('UI.DynamicViewEditor')],
    data: {
      breadcrumb: 'dynamicEditor.views'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./dynamic-view-section/dynamic-view-section.component').then(
            (c) => c.DynamicViewSectionComponent
          ),
        data: {
          breadcrumb: null
        }
      },
      {
        path: 'card',
        data: {
          breadcrumb: { label: 'dynamicEditor.card', link: null }
        },
        children: [
          {
            path: 'new',
            loadComponent: () =>
              import('./card-editor/card-editor.component').then(
                (c) => c.CardEditorComponent
              ),
            data: { breadcrumb: 'dynamicEditor.create' }
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./card-editor/card-editor.component').then(
                (c) => c.CardEditorComponent
              ),
            resolve: {
              dynamicCard: dynamicViewResolver
            },
            data: { breadcrumb: 'dynamicEditor.edit' }
          }
        ]
      },
      {
        path: 'section',
        data: {
          breadcrumb: { label: 'dynamicEditor.section', link: null }
        },
        children: [
          {
            path: 'new',
            loadComponent: () =>
              import('./section-editor/section-editor.component').then(
                (c) => c.SectionEditorComponent
              ),
            data: { breadcrumb: 'dynamicEditor.create' }
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./section-editor/section-editor.component').then(
                (c) => c.SectionEditorComponent
              ),
            resolve: {
              dynamicSection: dynamicViewResolver
            },
            data: { breadcrumb: 'dynamicEditor.edit' }
          }
        ]
      }
    ]
  }
];
